import { forwardRef, useCallback, useEffect, useImperativeHandle, useState, useContext } from 'react'
import { useDropzone } from 'react-dropzone'
import {
  FileText,
  FileType,
  FileSpreadsheet,
  Image as ImageIcon,
  Music,
  Link as LinkIcon,
  Film,
  Upload,
  Globe,
  Brain,
  Coins,
  FileSignature,
  Loader2,
  Trash2,
  CheckCircle2,
  Clock4,
  RefreshCcw,
  ChevronDown
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import KnowledgeMetaModal from '@/widgets/KnowledgeMetaModal'
import KnowledgeJsonModal from '@/widgets/KnowledgeJsonModal'
import ConfirmModal from '@/widgets/ConfirmModal'
import {
  uploadKnowledge,
  uploadKnowledgeLink,
  listKnowledge,
  removeKnowledge,
  summarizeKnowledge,
  fetchLinkPreview,
  getKnowledgeJson,
  buildDownloadUrl,
  createLinkKnowledge
} from '@/api/kuizy'
import { LIMIT_AI_TOKEN } from '@/settings/master'
import { LayoutContext } from '@/layout/KuizyLayout'

const KnowledgeUploader = forwardRef(function KnowledgeUploader(
  { examId, onUploaded, onBusyChange, onSummaryStats },
  ref
) {
  const { showLoading, hideLoading } = useContext(LayoutContext) || {}
  const [items, setItems] = useState([])
  const [link, setLink] = useState('')
  const [tokenCount, setTokenCount] = useState(0)
  const [summarizing, setSummarizing] = useState(false)
  const [jsonModalOpen, setJsonModalOpen] = useState(false)
  const [jsonFileInfo, setJsonFileInfo] = useState(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [itemToDelete, setItemToDelete] = useState(null)
  const [metaOpen, setMetaOpen] = useState(false)
  const [pendingFile, setPendingFile] = useState(null)
  const [confirmSumOpen, setConfirmSumOpen] = useState(false)
  const [sumFiles, setSumFiles] = useState([])
  const [combined, setCombined] = useState([])  // array of { id, name, summary }
  const [expandedCards, setExpandedCards] = useState({})

  const [showNameModal, setShowNameModal] = useState(false)
  const [pendingLink, setPendingLink] = useState(null)
  const [linkName, setLinkName] = useState("")


  const Icon = t =>
  ({
    pdf: FileText,
    word: FileType,
    excel: FileSpreadsheet,
    image: ImageIcon,
    audio: Music,
    video: Film
  }[t] || LinkIcon)

  const adjustToken = d => setTokenCount(p => Math.max(p + d, 0))

  const loadPreview = async (id, url) => {
    try {
      const { data } = await fetchLinkPreview(url)
      setItems(prev => prev.map(it => (it.id === id ? { ...it, preview: data } : it)))
    } catch { }
  }

  const recalcSummaryStats = (list) => {
    const total = list.length
    const summarized = list.filter(it => parseInt(it.token, 10) > 0).length
    const unsummarized = total - summarized
    const tokens = list.reduce((s, it) => s + (parseInt(it.token, 10) || 0), 0)
    onSummaryStats?.({ total, summarized, unsummarized, tokens })
  }

  useEffect(() => {
    onBusyChange?.(summarizing || items.some(it => it.stage !== 'done'))
    recalcSummaryStats(items)
  }, [items, summarizing])

  useEffect(() => {
    if (summarizing === false) loadCombinedSummary()
  }, [summarizing])

  const doUpload = useCallback(
    (file, meta) => {
      const tempId = `${Date.now()}_${file.name}`
      setItems(p => [
        ...p,
        {
          id: tempId,
          name: file.name,
          meta,
          type: '',
          stage: 'uploading',
          progress: 0,
          token: 0
        }
      ])
      const fd = new FormData()
      fd.append('file', file)
      fd.append('lesson_id', examId)
      fd.append('meta', JSON.stringify(meta))
      uploadKnowledge(fd, {
        onUploadProgress: e => {
          if (!e.total) return
          const pct = Math.round((e.loaded / e.total) * 100)
          setItems(prev =>
            prev.map(it =>
              it.id === tempId
                ? { ...it, progress: pct, stage: pct >= 100 ? 'processing' : 'uploading' }
                : it
            )
          )
        }
      })
        .then(({ data }) => {
          setItems(prev =>
            prev.map(it => (it.id === tempId ? { ...data, meta, stage: 'done', progress: 100 } : it))
          )
          onUploaded?.(data)
          loadCombinedSummary()
        })
        .catch(() => setItems(p => p.filter(it => it.id !== tempId)))
    },
    [examId, onUploaded]
  )

  const chooseFile = f => {
    setPendingFile(f)
    setMetaOpen(true)
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: files => files.forEach(chooseFile),
    accept: {
      'application/pdf': [],
      'application/msword': [],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [],
      'application/vnd.ms-excel': [],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [],
      'image/*': [],
      'audio/*': [],
      'video/*': []
    }
  })


  const addLink = async () => {
    const url = link.trim()
    if (!url) return
    const meta = { title: url, description: '', tags: [] }
    setItems(p => [
      ...p,
      { id: url, name: url, meta, type: 'link', stage: 'processing', progress: 100, token: 0 }
    ])
    try {
      const { data } = await createLinkKnowledge({ link: url, lesson_id: examId, meta, name: linkName || url })
      setItems(prev => prev.map(it => (it.id === url ? { ...data, meta, stage: 'done' } : it)))
      onUploaded?.(data)
      loadCombinedSummary()
      loadPreview(data.id, url)
      setLink('')
    } catch {
      setItems(p => p.filter(it => it.id !== url))
    }
  }

  const handleAddClick = () => {
    if (!link.trim()) return

    setPendingLink(link)

    // ตั้งชื่อ default จาก hostname
    try {
      const url = new URL(link)
      setLinkName(url.hostname)
    } catch {
      setLinkName("ลิงก์ใหม่")
    }

    setShowNameModal(true)
  }

  const confirmAddLink = () => {
    if (!linkName.trim()) return

    addLink({
      name: linkName,
      url: pendingLink
    })

    setShowNameModal(false)
    setLink("")
    setPendingLink(null)
    setLinkName("")
  }

  const removeItem = async itm => {
    await removeKnowledge(examId, itm.id)
    setItems(prev => {
      const updated = prev.filter(i => i.id !== itm.id)
      adjustToken(-(itm.token || 0))
      loadCombinedSummary(updated)
      return updated
    })
  }

  const loadInitial = async () => {
    if (!examId) return
    const { data } = await listKnowledge(examId)
    const ready = (data.files || []).map(d => ({ ...d, stage: 'done', progress: 100 }))
    setItems(ready)
    ready.forEach(it => {
      if (it.type === 'link' || it.type === 'youtube') loadPreview(it.id, it.url || it.name)
    })
    setTokenCount(ready.reduce((s, it) => s + (parseInt(it.token, 10) || 0), 0))
    loadCombinedSummary(ready)
  }

  useEffect(() => {
    loadInitial()
  }, [examId])

  const runSummarize = async () => {
    setSummarizing(true)
    if (showLoading) {
      showLoading({
        variant: 'imageSplit',
        text: 'กำลังสรุปเนื้อหา...',
        subtext: 'ประมวลผลด้วย AI กรุณารอสักครู่',
        imageUrl: '/images/write-exam-2.gif',
      })
    }
    setItems(prev => prev.map(it => (it.token ? it : { ...it, stage: 'summarizing' })))
    try {
      const { data } = await summarizeKnowledge(examId)
      const list = Array.isArray(data) ? data : data.files || []
      setItems(prev =>
        prev.map(it => {
          const found = list.find(d => d.id === it.id)
          if (found) return { ...it, token: found.token, stage: 'done' }
          return { ...it, stage: it.token ? 'done' : 'processing' }
        })
      )
      const add = list.reduce((s, x) => s + (parseInt(x.token, 10) || 0), 0)
      if (add) adjustToken(add)
    } finally {
      setSummarizing(false)
      loadInitial()
      if (hideLoading) hideLoading()
    }
  }

  const summarize = () => {
    const unsum = items.filter(it => !it.token)
    if (!unsum.length) return
    setSumFiles(unsum)
    setConfirmSumOpen(true)
  }

  const loadCombinedSummary = async (current = null) => {
    const list = current || items
    const summarized = list.filter(it => it.token)
    if (!summarized.length) {
      setCombined([])
      recalcSummaryStats(list)
      return
    }
    const parts = []
    for (const it of summarized) {
      try {
        const { data } = await getKnowledgeJson(examId, it.id)
        if (data?.summary) {
          parts.push({
            id: it.id,
            name: it.meta?.title || it.title || it.name,
            summary: data.summary
          })
        }
      } catch { }
    }
    setCombined(parts)
    recalcSummaryStats(list)
  }

  useImperativeHandle(ref, () => ({
    openSummarize: () => summarize(),
    getSummaryStats: () => {
      const total = items.length
      const summarized = items.filter(it => parseInt(it.token, 10) > 0).length
      const unsummarized = total - summarized
      const tokens = items.reduce((s, it) => s + (parseInt(it.token, 10) || 0), 0)
      return { total, summarized, unsummarized, tokens }
    }
  }))

  const badgeEl = (stage, tok) =>
    stage === 'summarizing' ? (
      <RefreshCcw className="text-primary w-4 h-4" />
    ) : tok ? (
      <CheckCircle2 className="text-green-500 w-4 h-4" />
    ) : (
      <Clock4 className="text-yellow-500 w-4 h-4" />
    )

  const fadeUp = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 } }

  return (
    <motion.div {...fadeUp} transition={{ duration: 0.35 }} className="flex flex-col gap-4">
      <label className="text-sm font-medium flex items-center gap-2">
        <Brain size={18} /> คลังเนื้อหา{' '}
        <code className="text-xs">
          {(tokenCount / 100).toLocaleString()} / {LIMIT_AI_TOKEN}
        </code>{' '}
        <Coins size={14} className="text-yellow-500" />
      </label>

      <div className="border border-gray-200 rounded-xl p-4 bg-white flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <AnimatePresence>
            {items.map(it => {
              const isLink = it.type === 'link' || it.type === 'youtube'
              const isImage = it.type === 'image'
              const thumb = isLink && it.preview?.image
              const Ico = Icon(it.type)
              return (
                <motion.div
                  key={it.id}
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                  className="relative flex flex-col rounded-xl border border-gray-200 bg-gray-50/60 shadow-sm hover:shadow-md transition-shadow overflow-hidden"
                >
                  {thumb ? (
                    <img src={thumb} alt="thumb" className="w-full h-28 object-cover" />
                  ) : isImage ? (
                    <img
                      src={buildDownloadUrl(it.id)}
                      alt="img"
                      className="w-full h-28 object-cover"
                    />
                  ) : (
                    <div className="w-full h-28 flex items-center justify-center bg-primary text-white">
                      <Ico size={46} />
                    </div>
                  )}

                  <div className="p-3 flex flex-col gap-2 flex-1 overflow-hidden">
                    {isLink ? (
                      <a
                        href={it.preview?.url || it?.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-semibold text-sm text-primary line-clamp-2 hover:underline"
                      >
                        {it.preview?.title || it.meta?.title || it.title || it.name}
                      </a>
                    ) : (
                      <a
                        href={buildDownloadUrl(it.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-semibold text-sm text-primary line-clamp-2 hover:underline"
                      >
                        {it.meta?.title || it.title || it.name}
                      </a>
                    )}

                    {(it.preview?.description || it.meta?.description || it.description) && (
                      <p className="text-xs text-gray-600 leading-5 line-clamp-4">
                        {it.preview?.description || it.meta?.description || it.description}
                      </p>
                    )}
                    {it.stage === 'processing' && (
                      <div className="text-xs text-pink-600 flex items-center gap-1">
                        <Loader2 className="animate-spin w-3.5 h-3.5" /> รอดำเนินการ
                      </div>
                    )}
                    {it.stage === 'summarizing' && (
                      <div className="text-xs text-pink-600 flex items-center gap-1">
                        <Loader2 className="animate-spin w-3.5 h-3.5" /> สรุป…
                      </div>
                    )}
                  </div>

                  <div className="absolute top-2 left-2 bg-white/90 rounded-full p-1">
                    {badgeEl(it.stage, it.token)}
                  </div>

                  <div className="absolute top-2 right-2 flex items-center gap-1 bg-white/90 rounded-md px-1.5 py-1">
                    {it.stage === 'done' && (
                      <>
                        <span
                          className="text-[10px] text-yellow-600 cursor-pointer flex items-center gap-0.5"
                          onClick={() => {
                            setJsonFileInfo({ eid: examId, fid: it.id })
                            setJsonModalOpen(true)
                          }}
                        >
                          {(it.token / 100 || 0).toLocaleString()} <Coins size={10} />
                        </span>
                        <button
                          title="ดูผล"
                          className="text-primary"
                          onClick={() => {
                            setJsonFileInfo({ eid: examId, fid: it.id })
                            setJsonModalOpen(true)
                          }}
                        >
                          <FileSignature size={14} />
                        </button>
                      </>
                    )}
                  </div>

                  <div className="absolute bottom-2 right-2 bg-white/90 rounded-md p-1">
                    <button
                      className="text-red-500"
                      onClick={() => {
                        setItemToDelete(it)
                        setConfirmOpen(true)
                      }}
                      title="ลบ"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>

                  {it.stage === 'uploading' && (
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200">
                      <div
                        className="h-full bg-primary transition-all"
                        style={{ width: `${it.progress || 0}%` }}
                      />
                    </div>
                  )}
                </motion.div>
              )
            })}
          </AnimatePresence>
        </div>

        <div
          {...getRootProps()}
          className="flex flex-col items-center justify-center text-primary border-2 border-dashed border-primary/60 rounded-xl h-28 cursor-pointer bg-primary/5 hover:bg-primary/10 transition-colors text-base"
        >
          <input {...getInputProps()} />
          <Upload size={18} className="mb-1" />
          {isDragActive ? 'ปล่อยไฟล์ที่นี่' : 'ลากไฟล์มาวาง หรือคลิกเพื่อเลือกไฟล์'}
        </div>

        <div className="flex items-center gap-2">
          <Globe size={18} className="text-gray-600" />
          <input
            value={link}
            onChange={e => setLink(e.target.value)}
            placeholder="วางลิงก์แล้วกด +"
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
          />
          <button
            onClick={handleAddClick}
            className="bg-primary hover:bg-secondary text-white rounded-lg px-3 py-2 text-sm font-semibold"
          >
            +
          </button>
        </div>

        {items.some(it => !it.token) && (
          <button
            disabled={summarizing}
            onClick={() => summarize()}
            className="inline-flex items-center gap-2 bg-primary hover:bg-secondary disabled:opacity-60 text-white rounded-lg px-4 py-2 text-sm font-semibold w-max"
          >
            {summarizing ? <Loader2 className="animate-spin w-4 h-4" /> : <RefreshCcw size={16} />} สรุปเนื้อหาจากคลังความรู้ด้วย AI
          </button>
        )}

        {combined.length > 0 && (
          <div className="border-t border-gray-200 pt-4">
            <h4 className="flex items-center gap-2 text-primary font-semibold mb-3">
              <Brain size={16} /> รวมสรุปเนื้อหา ({combined.length} ไฟล์)
            </h4>
            <div className="flex flex-col gap-2">
              {combined.map(card => {
                const isOpen = !!expandedCards[card.id]
                return (
                  <div
                    key={card.id}
                    className="rounded-xl border border-gray-200 bg-gray-50/60 shadow-sm overflow-hidden"
                  >
                    {/* Card Header / Toggle */}
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedCards(prev => ({ ...prev, [card.id]: !prev[card.id] }))
                      }
                      className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-100 transition-colors"
                    >
                      <span className="flex items-center gap-2 font-medium text-sm text-gray-800 min-w-0">
                        <Brain size={14} className="text-primary shrink-0" />
                        <span className="truncate">{card.name}</span>
                      </span>
                      <ChevronDown
                        size={16}
                        className={`text-gray-400 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                      />
                    </button>

                    {/* Collapsible Body */}
                    {isOpen && (
                      <div className="px-4 pb-4 pt-1 border-t border-gray-100">
                        <pre className="whitespace-pre-wrap text-sm text-gray-700 leading-relaxed max-h-64 overflow-auto">
                          {card.summary}
                        </pre>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>

      {confirmOpen && itemToDelete && (
        <ConfirmModal
          type="confirm"
          title="ยืนยันการลบ"
          message={`ต้องการลบ "${itemToDelete.meta?.title || itemToDelete.name}" ใช่หรือไม่?`}
          onConfirm={() => {
            removeItem(itemToDelete)
            setConfirmOpen(false)
          }}
          onCancel={() => setConfirmOpen(false)}
        />
      )}

      {jsonModalOpen && (
        <KnowledgeJsonModal
          visible={jsonModalOpen}
          fileInfo={jsonFileInfo}
          onClose={payload => {
            setJsonModalOpen(false)
            if (payload?.saved && payload.meta) {
              const { fid, title, description, summary } = payload.meta
              setItems(prev =>
                prev.map(it =>
                  it.id === fid
                    ? { ...it, meta: { ...(it.meta || {}), title, description, summary } }
                    : it
                )
              )
              loadCombinedSummary()
            }
          }}
        />
      )}

      {pendingFile && (
        <KnowledgeMetaModal
          file={pendingFile}
          open={metaOpen}
          onClose={({ ok, meta }) => {
            setMetaOpen(false)
            if (ok) doUpload(pendingFile, meta)
            setPendingFile(null)
          }}
        />
      )}

      {confirmSumOpen && (
        <ConfirmModal
          type="confirm"
          title="ยืนยันการสรุป"
          message={`ยืนยันสรุป ${sumFiles.length} ไฟล์ และอาจมีค่าใช้จ่ายตามจำนวนขนาดของไฟล์ที่อัพโหลด`}
          onConfirm={() => {
            setConfirmSumOpen(false)
            runSummarize()
          }}
          onCancel={() => setConfirmSumOpen(false)}
        />
      )}


      {showNameModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-md rounded-2xl shadow-xl p-6 space-y-4">

            <h2 className="text-lg font-semibold">ตั้งชื่อสำหรับลิงก์</h2>

            <div className="space-y-2">
              <label className="text-sm text-gray-600">ชื่อที่จะแสดง</label>
              <input
                value={linkName}
                onChange={(e) => setLinkName(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
                placeholder="กรอกชื่อ"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setShowNameModal(false)}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-100"
              >
                ยกเลิก
              </button>

              <button
                onClick={confirmAddLink}
                className="px-4 py-2 text-sm rounded-lg bg-primary text-white hover:bg-secondary font-semibold"
              >
                บันทึก
              </button>
            </div>

          </div>
        </div>
      )}

    </motion.div>
  )
})

export default KnowledgeUploader
